import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '@coeldery/chat-thread';

/**
 * useChatStream — 前端接 `/api/chat`（SSE）
 *
 * ⚠️ 為何 app-local：庫內**冇**任何「前端 SSE client」模組（`worker-sse-tools` 係 Worker 側，
 *   而且狀態「實驗中」）→ 依 R3 記錄為回流候選（**P-44**）。
 *
 * 契約（對應 `functions/_lib/chat/sse.ts`）：
 *   `session` → `delta`＋ → `done`（另有 `error`）
 *   `done.songAllowed === false` ＝ 安全層 `hold_song`（乙案：延後作歌，唔係禁止）
 *
 * ⭐ **身份（0006；`decisions/018`）**：
 *   · `sessionId` ＝ 一個**對話**（訊息 thread ＋ 該次出歌流程）
 *   · `deviceId`  ＝ **一部機**（個人 library／播放清單）→ 開新對話唔會唔見歌
 *   ⚠️ 兩者都係**弱身份，唔係登入**（`P-45`）
 */

export interface ChatStreamState {
  messages: ChatMessage[];
  pending: boolean;
  /** 已誠實講明嘅錯誤（i18n key 或 provider 原文） */
  errorKey: string | null;
  errorDetail: string | null;
  songAllowed: boolean | null;
  /** 歷史載入完成未（避免「空狀態」閃一下） */
  hydrated: boolean;
}

const SESSION_STORAGE_KEY = 'cosingery.sessionId';
/** ⭐ 裝置身份（0006；`decisions/018`）—— **弱身份，唔係登入**（`P-45`）
 *  ⚠️ 2026-09-23：**清瀏覽器資料就會冇咗 → library 變空**（歌仍然在 DB）→ 所以要靠
 *  `src/deviceRecovery.ts` 嘅「恢復碼」路徑救返（`?device=`／「我的」輸入）
 */
export const DEVICE_STORAGE_KEY = 'cosingery.deviceId';

/** 讀返（冇就生成一個並記住）；私隱模式唔會拋 */
function readOrCreate(key: string): string {
  try {
    const saved = localStorage.getItem(key);
    if (saved) return saved;
  } catch {
    /* 私隱模式 → 用臨時 id（唔會跨 reload） */
  }
  const id = crypto.randomUUID();
  try {
    localStorage.setItem(key, id);
  } catch {
    /* 忽略 */
  }
  return id;
}

export function getSessionId(): string {
  return readOrCreate(SESSION_STORAGE_KEY);
}

/**
 * ⭐ 裝置身份（0006）：**同一部機嘅全部歌**（個人 library／播放清單）靠佢跨對話
 *   ⚠️ 佢同 `sessionId` 一樣係**弱身份**（本機隨機 uuid）→ **唔係登入**、唔可以當憑證（`P-45`）
 */
export function getDeviceId(): string {
  return readOrCreate(DEVICE_STORAGE_KEY);
}

/**
 * ⭐ **2026-09-23：直接設定裝置身份**（只由「恢復」路徑用；`src/deviceRecovery.ts`）——
 *   ⚠️ 唔准喺其他位亂寫（deviceId **就係 library 嘅鎖匙**）。
 */
export function setDeviceId(id: string): void {
  try {
    localStorage.setItem(DEVICE_STORAGE_KEY, id);
  } catch {
    /* 私隱模式：寫唔到就照用（當次有效） */
  }
}

/** ⭐ 開新對話：換一個新 `sessionId` 並記住（舊對話嘅歌仍然喺個人 library） */
export function startNewSession(): string {
  const id = crypto.randomUUID();
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, id);
  } catch {
    /* 忽略 */
  }
  return id;
}

/**
 * ⭐ UI v2 步 4：由**外殼**（右側抽屜嘅「之前嘅對話」）轉去指定對話。
 *   只寫低 `sessionId`；配合 ChatPage remount（App 用 `key`）就會讀返該對話。
 *   ⚠️ 同 hook 內嘅 `openSession()` 同義，只係唔需要 hook 實例。
 */
export function openSessionId(id: string): void {
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, id);
  } catch {
    /* 私隱模式：寫唔到 → 今次仍然可以喺 session 內轉 */
  }
}

export function useChatStream(locale: string) {
  const [state, setState] = useState<ChatStreamState>({
    messages: [],
    pending: false,
    errorKey: null,
    errorDetail: null,
    songAllowed: null,
    hydrated: false,
  });
  /** ⭐ 0006：`sessionId` 由 **ref 變 state** —— 開新對話要即刻換 thread（`decisions/018`） */
  const [sessionId, setSessionId] = useState<string>(() => getSessionId());
  const [deviceId] = useState<string>(() => getDeviceId());
  /** ⭐ 0007：後端寫咗新訊息（歌詞／歌）之後，叫返呢個 tick 重新載入歷史 */
  const [reloadTick, setReloadTick] = useState(0);
  const lastTextRef = useRef<string>('');

  /**
   * ⚠️ 開頁／**換對話**要**讀返歷史**（2026-09-20 產品負責人實測：離開頁再返嚟訊息唔見咗 ✗）
   *   ⭐ 2026-09-21（`decisions/018`）：改為**跟 `sessionId`** —— 開新對話即刻清空並讀新 thread
   */
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, messages: [], hydrated: false, pending: false, errorKey: null, errorDetail: null }));
    (async () => {
      try {
        const res = await fetch(`/api/chat/sessions/${sessionId}`);
        const data = (await res.json().catch(() => ({}))) as {
          messages?: {
            id: string;
            role: 'user' | 'assistant';
            text: string;
            safetyAction?: string | null;
            /** ⭐ 0007（`decisions/025`）：訊息種類（text／understanding／lyrics／song） */
            kind?: 'text' | 'understanding' | 'lyrics' | 'song';
            songId?: string | null;
          }[];
        };
        if (!alive) return;
        setState((s) => ({
          ...s,
          hydrated: true,
          messages: (data.messages ?? []).map((m) => ({
            id: m.id,
            role: m.role,
            text: m.text,
            state: 'done' as const,
            noteKey: m.safetyAction === 'hold_song' ? 'chat_hold_note' : undefined,
            /** ⭐ 0007（`decisions/025`）：訊息種類 ＋ 歌曲 id（前端靠佢掛動作） */
            kind: m.kind ?? 'text',
            songId: m.songId ?? null,
          })),
        }));
      } catch {
        /** 讀唔到歷史唔應該阻住傾偈（但亦唔准假裝讀到） */
        if (alive) setState((s) => ({ ...s, hydrated: true }));
      }
    })();
    return () => {
      alive = false;
    };
  }, [sessionId, reloadTick]);

  /**
   * ⭐ **開新對話**（`decisions/018`）：換一個新 session → 對話清空、歌曲流程由頭開始；
   *   ⚠️ 舊對話嘅歌**唔會消失**（靠 `deviceId` 嘅個人 library）
   */
  const startNewChat = useCallback(() => {
    setSessionId(startNewSession());
  }, []);

  const send = useCallback(
    async (text: string, opts: { newChat?: boolean; /** ⭐ `decisions/027`：`listener`（預設）／`chat` */ mode?: 'listener' | 'chat' } = {}) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      /**
       * ⭐ **自動開新對話**（`decisions/018`；產品負責人指示「歌出完之後應該由新開始」）：
       *   喺**送之前**換 session，並用**新 id** 送 → 唔會撞 React state 未更新嘅 stale closure。
       *   ⚠️ 舊歌唔會消失（佢仍然喺 `deviceId` 嘅個人 library）
       */
      let sid = sessionId;
      if (opts.newChat) {
        sid = startNewSession();
        setSessionId(sid);
        setState((s) => ({ ...s, messages: [] }));
      }
      lastTextRef.current = trimmed;
      const assistantId = crypto.randomUUID();
      setState((s) => ({
        ...s,
        pending: true,
        errorKey: null,
        errorDetail: null,
        messages: [
          ...s.messages,
          { id: crypto.randomUUID(), role: 'user', text: trimmed },
          { id: assistantId, role: 'assistant', text: '', state: 'streaming' },
        ],
      }));

      const patchAssistant = (patch: Partial<ChatMessage>, extra?: Partial<ChatStreamState>) => {
        setState((s) => ({
          ...s,
          ...extra,
          messages: s.messages.map((m) => (m.id === assistantId ? { ...m, ...patch } : m)),
        }));
      };

      try {
        const res = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: sid,
            deviceId,
            text: trimmed,
            locale,
            /** ⭐ `decisions/027`：模式**只影響人格／語氣** —— 寫歌步驟唔理模式 */
            mode: opts.mode ?? 'listener',
          }),
        });
        if (!res.ok) {
          patchAssistant({ state: 'error', text: '' }, { pending: false, errorKey: 'chat_error_stream' });
          return;
        }
        const reader = res.body?.getReader();
        if (!reader) {
          patchAssistant({ state: 'error' }, { pending: false, errorKey: 'chat_error_stream' });
          return;
        }
        const decoder = new TextDecoder();
        let buffer = '';
        let text2 = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const blocks = buffer.split('\n\n');
          buffer = blocks.pop() ?? '';
          for (const block of blocks) {
            const evLine = block.split('\n').find((l) => l.startsWith('event:'));
            const dataLine = block.split('\n').find((l) => l.startsWith('data:'));
            if (!evLine || !dataLine) continue;
            const event = evLine.slice(6).trim();
            let payload: { text?: string; songAllowed?: boolean; delayTurns?: number; code?: string; message?: string } = {};
            try {
              payload = JSON.parse(dataLine.slice(5).trim());
            } catch {
              continue;
            }
            if (event === 'delta' && typeof payload.text === 'string') {
              text2 += payload.text;
              patchAssistant({ text: text2, state: 'streaming' });
            } else if (event === 'done') {
              patchAssistant(
                {
                  text: text2,
                  state: 'done',
                  noteKey: payload.songAllowed === false ? 'chat_hold_note' : undefined,
                },
                { pending: false, songAllowed: payload.songAllowed ?? null },
              );
            } else if (event === 'error') {
              patchAssistant(
                { text: text2, state: 'error' },
                {
                  pending: false,
                  errorKey: payload.code === 'model_failed' || payload.code === 'model_not_configured'
                    ? 'chat_error_model'
                    : 'chat_error_stream',
                  errorDetail: payload.message ?? null,
                },
              );
            }
          }
        }
      } catch (err) {
        patchAssistant({ state: 'error' }, { pending: false, errorKey: 'chat_error_stream', errorDetail: String(err).slice(0, 160) });
      }
    },
    [locale, sessionId, deviceId],
  );

  const retry = useCallback(() => {
    setState((s) => ({ ...s, messages: s.messages.filter((_, i) => i < s.messages.length - 1) }));
    void send(lastTextRef.current);
  }, [send]);

  /**
   * ⭐ 0007／`decisions/025`：
   *   · `openSession(id)` ＝ 轉去一個**舊對話**（頂欄「之前嘅對話」）
   *   · `reload()`        ＝ 重新讀歷史（後端寫咗歌詞／歌訊息之後即刻顯示）
   */
  const openSession = useCallback((id: string) => {
    try {
      localStorage.setItem(SESSION_STORAGE_KEY, id);
    } catch {
      /* 私隱模式：仍然可以喺今次 session 內轉 */
    }
    setSessionId(id);
  }, []);
  const reload = useCallback(() => setReloadTick((n) => n + 1), []);

  return { state, send, retry, sessionId, deviceId, startNewChat, openSession, reload };
}
